/**
 * DemoWorkbenchShell — reusable routed shell for non-monograph demo apps.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import React, { Suspense, useCallback, useEffect, useId, useMemo, useState } from "react";
import { Command as CommandIcon, Search } from "lucide-react";
import { DemoNav } from "../catalog/index.js";
import { PRIMARY } from "../design-system/tokens.js";
import { useDocumentKeyDown, useUrlHashRoute } from "../hooks/index.js";
import { cssProperties } from "../styling/index.js";
import { isEditableElementTarget } from "../utils/index.js";
import { CommandPalette, useCommandPaletteShortcut } from "./command/index.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { KeyboardShortcutHints, KeyboardShortcutsDialog } from "./KeyboardShortcuts.js";
import type { KeyboardShortcut } from "./KeyboardShortcuts.js";
import { PanelLoadingFallback } from "./PanelLoadingFallback.js";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./primitives/index.js";
import { SkipLink } from "./SkipLink.js";

export interface DemoWorkbenchPanel<Id extends string = string> {
  readonly id: Id;
  readonly label: string;
  readonly component: React.ElementType;
  readonly accent?: string;
  readonly group?: string;
  readonly hint?: React.ReactNode;
  readonly icon?: React.ReactNode;
  readonly keywords?: readonly string[];
}

export interface DemoWorkbenchTheme {
  readonly accent?: string;
  readonly background?: string;
  readonly border?: string;
  readonly muted?: string;
  readonly navBackground?: string;
  readonly navText?: string;
  readonly surface?: string;
  readonly text?: string;
}

export interface DemoWorkbenchShellProps<
  Id extends string = string,
  Panel extends DemoWorkbenchPanel<Id> = DemoWorkbenchPanel<Id>,
> {
  readonly title: string;
  readonly panels: readonly Panel[];
  readonly currentScript?: string;
  readonly footer?: React.ReactNode;
  readonly groupLabels?: Readonly<Record<string, string>>;
  readonly groupOrder?: readonly string[];
  readonly hashKey?: string;
  readonly idBase?: string;
  readonly initialPanelId?: Id;
  readonly maxWidth?: number;
  readonly renderAfterPanel?: (panel: Panel) => React.ReactNode;
  readonly renderBeforePanel?: (panel: Panel) => React.ReactNode;
  readonly subtitle?: React.ReactNode;
  readonly theme?: DemoWorkbenchTheme;
}

const DEFAULT_THEME: Required<DemoWorkbenchTheme> = {
  accent: PRIMARY[500],
  background: "#0a0f1c",
  border: "rgba(148, 163, 184, 0.24)",
  muted: "#94a3b8",
  navBackground: "rgba(12, 22, 41, 0.92)",
  navText: "#e2e8f0",
  surface: "rgba(15, 23, 42, 0.74)",
  text: "#e8f0f8",
};

function themeValue<Key extends keyof DemoWorkbenchTheme>(
  theme: DemoWorkbenchTheme | undefined,
  key: Key,
): Required<DemoWorkbenchTheme>[Key] {
  return theme?.[key] ?? DEFAULT_THEME[key];
}

function groupOrderFor<Id extends string>(
  panels: readonly DemoWorkbenchPanel<Id>[],
  groupOrder: readonly string[] | undefined,
): readonly string[] {
  if (groupOrder !== undefined) return groupOrder;
  const groups: string[] = [];
  for (const panel of panels) {
    const group = panel.group ?? "Panels";
    if (!groups.includes(group)) groups.push(group);
  }
  return groups;
}

interface WorkbenchPanelGroup<Panel extends DemoWorkbenchPanel> {
  readonly id: string;
  readonly panels: readonly Panel[];
}

function groupedPanelsFor<Panel extends DemoWorkbenchPanel>(
  panels: readonly Panel[],
  groupOrder: readonly string[] | undefined,
): readonly WorkbenchPanelGroup<Panel>[] {
  const grouped = new Map<string, Panel[]>();
  for (const panel of panels) {
    const group = panel.group ?? "Panels";
    const groupPanels = grouped.get(group);
    if (groupPanels === undefined) grouped.set(group, [panel]);
    else groupPanels.push(panel);
  }
  return groupOrderFor(panels, groupOrder)
    .map((group) => ({ id: group, panels: grouped.get(group) ?? [] }))
    .filter((group) => group.panels.length > 0);
}

// Cap input length before applying regexes to prevent polynomial worst-case
// backtracking on pathological inputs (ReDoS, CWE-1333).
const MAX_SEGMENT_INPUT = 512;

function sanitizeDomIdSegment(value: string): string {
  const sanitized = value
    .slice(0, MAX_SEGMENT_INPUT)
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return sanitized.length > 0 ? sanitized : "panel";
}

function sanitizeReactId(value: string): string {
  return value.slice(0, MAX_SEGMENT_INPUT).replace(/[^A-Za-z0-9_-]+/g, "");
}

function buildTabIds<Id extends string>(
  panels: readonly DemoWorkbenchPanel<Id>[],
  baseId: string,
): ReadonlyMap<Id, string> {
  const ids = new Map<Id, string>();
  const seen = new Map<string, number>();
  for (const panel of panels) {
    const segment = sanitizeDomIdSegment(panel.id);
    const count = (seen.get(segment) ?? 0) + 1;
    seen.set(segment, count);
    ids.set(panel.id, `${baseId}-tab-${segment}${count > 1 ? `-${count}` : ""}`);
  }
  return ids;
}

export function DemoWorkbenchShell<
  Id extends string = string,
  Panel extends DemoWorkbenchPanel<Id> = DemoWorkbenchPanel<Id>,
>({
  title,
  panels,
  currentScript,
  footer,
  groupLabels,
  groupOrder,
  hashKey = "panel",
  idBase,
  initialPanelId,
  maxWidth = 1200,
  renderAfterPanel,
  renderBeforePanel,
  subtitle,
  theme,
}: DemoWorkbenchShellProps<Id, Panel>): React.JSX.Element {
  const firstPanel = panels[0];
  if (firstPanel === undefined) {
    throw new RangeError("DemoWorkbenchShell requires at least one panel");
  }

  const panelIds = useMemo(() => new Set<string>(panels.map((panel) => panel.id)), [panels]);
  const fallback =
    initialPanelId !== undefined && panelIds.has(initialPanelId) ? initialPanelId : firstPanel.id;
  const isPanelId = useMemo(
    () =>
      function isPanelId(value: string): value is Id {
        return panelIds.has(value);
      },
    [panelIds],
  );
  const [activeId, navigateToPanel] = useUrlHashRoute<Id>(hashKey, fallback, isPanelId);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const activeIndex = useMemo(
    () =>
      Math.max(
        0,
        panels.findIndex((panel) => panel.id === activeId),
      ),
    [activeId, panels],
  );
  const active = panels[activeIndex] ?? firstPanel;
  const ActivePanel = active.component;
  const reactId = useId();
  const domBaseId = idBase ?? `demo-workbench-${sanitizeReactId(reactId)}`;
  const tabpanelId = `${domBaseId}-panel`;
  const tabIds = useMemo(() => buildTabIds(panels, domBaseId), [domBaseId, panels]);
  const activeTabId =
    tabIds.get(active.id) ?? `${domBaseId}-tab-${sanitizeDomIdSegment(active.id)}`;

  const accent = active.accent ?? themeValue(theme, "accent");
  const background = themeValue(theme, "background");
  const border = themeValue(theme, "border");
  const muted = themeValue(theme, "muted");
  const navBackground = themeValue(theme, "navBackground");
  const navText = themeValue(theme, "navText");
  const surface = themeValue(theme, "surface");
  const text = themeValue(theme, "text");
  const panelGroups = useMemo(() => groupedPanelsFor(panels, groupOrder), [groupOrder, panels]);
  const shellStyle = cssProperties({
    "--demo-workbench-accent": accent,
    "--demo-workbench-background": background,
    "--demo-workbench-border": border,
    "--demo-workbench-muted": muted,
    "--demo-workbench-nav-bg": navBackground,
    "--demo-workbench-nav-text": navText,
    "--demo-workbench-surface": surface,
    "--demo-workbench-text": text,
  });

  const openPalette = useCallback(() => {
    setPaletteOpen(true);
  }, []);

  useCommandPaletteShortcut(setPaletteOpen);

  useEffect(() => {
    document.title = `${title} · ${active.label}`;
  }, [active.label, title]);

  useDocumentKeyDown((event) => {
    if (isEditableElementTarget(event.target)) return;
    if (event.key === "ArrowLeft") {
      event.preventDefault();
      const previous = panels[Math.max(0, activeIndex - 1)];
      if (previous !== undefined) navigateToPanel(previous.id);
      return;
    }
    if (event.key === "ArrowRight") {
      event.preventDefault();
      const next = panels[Math.min(panels.length - 1, activeIndex + 1)];
      if (next !== undefined) navigateToPanel(next.id);
      return;
    }
    if (!event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;
    const numeric = Number.parseInt(event.key, 10);
    if (Number.isInteger(numeric) && numeric >= 1 && numeric <= panels.length) {
      const panel = panels[numeric - 1];
      if (panel !== undefined) {
        event.preventDefault();
        navigateToPanel(panel.id);
      }
    }
  });

  const paletteItems = useMemo(
    () =>
      panels.map((panel) => ({
        group: panel.group ?? "Panels",
        hint: panel.hint,
        id: panel.id,
        keywords: [panel.id, panel.label, ...(panel.keywords ?? [])],
        label: panel.label,
        onSelect: (): void => {
          navigateToPanel(panel.id);
        },
      })),
    [navigateToPanel, panels],
  );

  const shortcuts = useMemo<readonly KeyboardShortcut[]>(
    () => [
      { keys: ["Cmd", "K"], label: "Open command palette (Mac)", group: "Navigation" },
      { keys: ["Ctrl", "K"], label: "Open command palette (Windows/Linux)", group: "Navigation" },
      { keys: ["Left"], label: "Previous panel", group: "Navigation" },
      { keys: ["Right"], label: "Next panel", group: "Navigation" },
      { keys: ["Alt", "1"], label: "Jump to first panel", group: "Navigation" },
      { keys: "?", label: "Show keyboard shortcuts", group: "Help" },
    ],
    [],
  );

  return (
    <div
      data-theme="light"
      className="min-h-screen text-[var(--demo-workbench-text)] [background:var(--demo-workbench-background)]"
      style={shellStyle}
    >
      <SkipLink
        targetId={tabpanelId}
        label="Skip to panel"
        style={{ background: accent, borderColor: "transparent", color: background }}
      />

      <header className="sticky top-0 z-50 flex flex-wrap items-center gap-3 border-b border-[var(--demo-workbench-border)] bg-[var(--demo-workbench-nav-bg)] px-4 py-3 text-[var(--demo-workbench-nav-text)] shadow-[0_14px_34px_rgba(42,50,70,0.08)] backdrop-blur-xl">
        <div className="min-w-[220px]">
          <div className="text-[14px] font-extrabold text-[var(--demo-workbench-nav-text)]">
            {title}
          </div>
          {subtitle !== undefined ? (
            <div className="mt-0.5 text-[11px] text-[var(--demo-workbench-muted)]">{subtitle}</div>
          ) : null}
        </div>

        <button
          type="button"
          onClick={openPalette}
          className="inline-flex min-h-9 cursor-pointer items-center gap-2 rounded-lg border border-[var(--demo-workbench-border)] bg-white/60 px-3 py-1.5 text-[12px] font-semibold text-[var(--demo-workbench-nav-text)] shadow-sm transition hover:bg-white/80"
        >
          <Search aria-hidden="true" size={14} />
          <span>Search</span>
          <kbd className="font-mono text-[var(--demo-workbench-muted)]">⌘K</kbd>
        </button>

        <KeyboardShortcutsDialog
          shortcuts={shortcuts}
          description="Keyboard commands available in this demo workspace."
        />

        <TooltipProvider delayDuration={180}>
          <nav
            role="tablist"
            aria-label={`${title} panels`}
            className="flex flex-[1_1_480px] flex-wrap items-center justify-end gap-2"
          >
            {panelGroups.map((group) => {
              return (
                <div key={group.id} className="inline-flex flex-wrap items-center gap-1.5">
                  <span className="text-[10.5px] font-extrabold text-[var(--demo-workbench-muted)]">
                    {groupLabels?.[group.id] ?? group.id}
                  </span>
                  {group.panels.map((panel) => {
                    const isActive = panel.id === active.id;
                    const panelAccent = panel.accent ?? accent;
                    const tabId =
                      tabIds.get(panel.id) ?? `${domBaseId}-tab-${sanitizeDomIdSegment(panel.id)}`;
                    return (
                      <Tooltip key={panel.id}>
                        <TooltipTrigger asChild>
                          <button
                            type="button"
                            id={tabId}
                            role="tab"
                            aria-selected={isActive}
                            aria-controls={tabpanelId}
                            onClick={() => {
                              navigateToPanel(panel.id);
                            }}
                            className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[12px] transition hover:-translate-y-px hover:bg-white/70"
                            style={{
                              background: isActive ? `${panelAccent}22` : "rgba(255,255,255,0.05)",
                              border: `1px solid ${isActive ? `${panelAccent}88` : border}`,
                              color: isActive ? text : muted,
                              fontWeight: isActive ? 800 : 600,
                            }}
                          >
                            {panel.icon !== undefined ? (
                              <span aria-hidden="true">{panel.icon}</span>
                            ) : null}
                            <span>{panel.label}</span>
                          </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom">
                          <span>{panel.label}</span>
                          {panel.hint !== undefined ? (
                            <span className="opacity-70"> · {panel.hint}</span>
                          ) : null}
                        </TooltipContent>
                      </Tooltip>
                    );
                  })}
                </div>
              );
            })}
          </nav>
        </TooltipProvider>
      </header>

      <main
        id={tabpanelId}
        role="tabpanel"
        aria-labelledby={activeTabId}
        tabIndex={-1}
        className="mx-auto px-5 pb-[72px] pt-6 outline-none"
        style={{ maxWidth }}
      >
        <div className="mb-4">
          <KeyboardShortcutHints shortcuts={shortcuts} maxItems={3} />
        </div>
        <ErrorBoundary
          key={active.id}
          fallbackTitle={`${active.label} failed to render`}
          recoveryLabel="Reset panel"
        >
          {renderBeforePanel !== undefined ? renderBeforePanel(active) : null}
          <Suspense
            fallback={
              <PanelLoadingFallback
                accentColor={accent}
                label={`Loading ${active.label}...`}
                maxWidth={maxWidth}
                minHeight={360}
                theme={{ border, muted, surface, text }}
              />
            }
          >
            {React.createElement(ActivePanel)}
          </Suspense>
          {renderAfterPanel !== undefined ? renderAfterPanel(active) : null}
        </ErrorBoundary>
        {footer !== undefined ? (
          <footer className="mt-7 border-t border-[var(--demo-workbench-border)] pt-3.5 text-[11.5px] font-semibold text-[var(--demo-workbench-muted)]">
            {footer}
          </footer>
        ) : null}
      </main>

      {currentScript !== undefined ? (
        <>
          <DemoNav currentScript={currentScript} />
          <div aria-hidden="true" style={{ height: 56 }} />
        </>
      ) : null}

      <CommandPalette
        open={paletteOpen}
        onOpenChange={setPaletteOpen}
        items={paletteItems}
        title={
          <span style={{ alignItems: "center", display: "inline-flex", gap: 8 }}>
            <CommandIcon aria-hidden="true" size={18} />
            Jump to panel
          </span>
        }
        description={`Navigate ${title} without leaving the keyboard.`}
        placeholder="Search panels..."
        ariaLabel={`${title} command palette`}
        emptyLabel="No panel matches that query."
      />
    </div>
  );
}
